/**
 * Phase 7 hotfix round 7 (R7-#2, 2026-05-26) — Confirmation dialog для delete
 * контракта.
 *
 * Mirror pattern UnavailableModal/PaywallModal: overlay 50% bg-black/50,
 * центральная карточка bg-canvas radius 20, role=dialog + aria-modal +
 * aria-labelledby. Initial focus на Cancel (safer default для destructive
 * action). Esc + overlay click = cancel (consistent с UnavailableModal).
 *
 * **Destructive variant** vs UnavailableModal: 2 кнопки (Cancel + Delete),
 * Delete styled через `bg-error` (#dc2626 token из tailwind.config.ts). B-049
 * «3rd consumer trigger» правило соблюдено — это 4-й modal в проекте, refactor
 * в shared `<ConfirmDialog/>` отложен как отдельная задача, эти 2 новых hotfix
 * модалки реализованы независимо для scope-tight commit'а.
 *
 * a11y: role=dialog, aria-modal, aria-labelledby. Initial focus на Cancel
 * (защита от accidental Enter). Закрытие: overlay click, Escape, кнопка Cancel.
 */

import { useEffect, useRef } from 'react';

export interface DeleteContractDialogProps {
  isOpen: boolean;
  contractName: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export function DeleteContractDialog({
  isOpen,
  contractName,
  onCancel,
  onConfirm,
}: DeleteContractDialogProps) {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onCancel]);

  useEffect(() => {
    if (isOpen) cancelButtonRef.current?.focus();
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      onClick={onCancel}
      style={{ position: 'fixed', inset: 0, zIndex: 100, padding: 20 }}
      className="flex items-center justify-center bg-black/50"
      data-testid="delete-contract-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-contract-dialog-title"
        className="w-full bg-canvas text-center"
        style={{
          maxWidth: 360,
          borderRadius: 20,
          padding: 24,
          boxShadow: '0 16px 64px rgba(0,0,0,.4)',
        }}
      >
        <h2
          id="delete-contract-dialog-title"
          className="font-bold text-ink leading-snug"
          style={{ fontSize: 19, margin: '0 0 24px' }}
          data-testid="delete-contract-dialog-title"
        >
          Удалить договор «{contractName}»? Это действие нельзя отменить.
        </h2>
        <div className="flex flex-col gap-2.5">
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onCancel}
            aria-label="Отмена"
            data-testid="delete-contract-cancel"
            className={
              'w-full font-bold bg-card text-ink border-[1.5px] border-accent ' +
              'min-h-tap-chip-lg rounded-2xl ' +
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:ring-offset-2 ' +
              'focus-visible:outline-none active:opacity-90 transition-opacity touch-manipulation'
            }
            style={{ fontSize: 19 }}
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={onConfirm}
            aria-label="Удалить договор"
            data-testid="delete-contract-confirm"
            className={
              'w-full font-bold bg-error text-white ' +
              'min-h-tap-chip-lg rounded-2xl ' +
              'focus-visible:ring-2 focus-visible:ring-error focus-visible:ring-offset-2 ' +
              'focus-visible:outline-none active:opacity-90 transition-opacity touch-manipulation'
            }
            style={{ fontSize: 19 }}
          >
            Удалить
          </button>
        </div>
      </div>
    </div>
  );
}
